// ============================================================
// AcceptInviteScreen.tsx
// Team Invites — the public Accept Invite page. Reached from the
// invite link in the user's email; App.tsx's URL-parser useEffect
// pulls ?token=…, stashes it in the store, and flips authView to
// 'acceptInvite'. Cloned from AcceptResetScreen with the same nav
// bar + casevo-brand styling + axios error handling.
//
// Three render branches:
//   - loading      → spinner while verifyInviteApi runs on mount
//   - tokenInvalid → "Invite invalid or expired" + Back-to-login CTA
//   - default      → the firstName / lastName / password form
//
// On success the screen calls setAuth(token, user) with the response
// from acceptInviteApi (same AuthResponse shape as loginApi /
// registerApi return) so the user is logged in immediately — no
// post-submit redirect to login, no second password entry.
// ============================================================
import React, { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { ErrorNote } from '../shared/ActionBox'
import { verifyInviteApi, acceptInviteApi } from '../../api/casevoApi'
import { useCasevoStore } from '../../store/useCasevoStore'

interface AcceptInviteScreenProps {
  token: string
  onShowLogin: () => void
  // Called after a successful accept (setAuth done) so App can exit the
  // invite deep link and reveal the authenticated app.
  onAccepted?: () => void
}

// Shape returned by verifyInviteApi — pinned locally so the form can
// surface a meaningful heading ("You've been invited to join {firmName}")
// and confirm which email the invite is for. role is read but not
// rendered today; kept on the type so the future copy can pull it in
// without another API trip.
interface InviteDetails {
  email: string
  role: string
  firmName: string
}

// Per-field validation errors. Mirrors AcceptResetScreen / LoginScreen
// — single banner above the form is reserved for server-side errors
// (validation, expired token, network failures), per-field messages
// render inline below the input.
type FieldErrors = Partial<Record<'firstName' | 'lastName' | 'password', string>>

const AcceptInviteScreen: React.FC<AcceptInviteScreenProps> = ({ token, onShowLogin, onAccepted }) => {
  const setAuth = useCasevoStore((s) => s.setAuth)
  // Mount-time verify state. invite=null + verifying=true → spinner.
  // verifying=false + invite=null + tokenInvalid → invalid-link view.
  // invite=non-null → form view.
  const [verifying, setVerifying] = useState(true)
  const [invite, setInvite] = useState<InviteDetails | null>(null)
  const [tokenInvalid, setTokenInvalid] = useState(false)
  // Form state.
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  // Submit-time state — independent of the mount-time verify state.
  // error is for banner messages (server-side validation, expired-on-
  // submit, network); tokenInvalid above handles the mount-time path.
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  // Focus the firstName field as soon as the form view mounts so the
  // user can start typing immediately. The ref is null on the loading
  // / tokenInvalid branches but ?.focus() is a no-op there so nothing
  // to guard against.
  const firstNameRef = useRef<HTMLInputElement>(null)
  // Mount-time verify. Pre-flights the token via GET /api/invites/verify
  // so we can render the form with the firm name + email before asking
  // the user to fill anything in — and so we can show the invalid-link
  // state if the token is bad rather than letting the user fill in the
  // form and hit submit just to see an error.
  useEffect(() => {
    let cancelled = false
    if (!token) {
      // Should never happen — App.tsx's parser only routes here when a
      // token is present — but defensive: treat as invalid.
      setTokenInvalid(true)
      setVerifying(false)
      return
    }
    verifyInviteApi(token)
      .then((details) => {
        if (cancelled) return
        setInvite(details)
        setVerifying(false)
      })
      .catch(() => {
        // Any failure on verify → invalid-link state. The specific
        // backend message (expired, revoked, malformed) isn't surfaced
        // here because the only useful next action is Back to login;
        // a noisy error message wouldn't change what the user can do.
        if (cancelled) return
        setTokenInvalid(true)
        setVerifying(false)
      })
    return () => { cancelled = true }
  }, [token])
  // Focus the firstName field when the form view becomes visible.
  // Depends on `invite` so the focus fires the moment the form renders
  // (i.e. after verifying flips to false and invite lands).
  useEffect(() => {
    if (invite) firstNameRef.current?.focus()
  }, [invite])
  // Client-side validation rules. Caller decides what to do with the
  // result (set state vs. proceed). Password matches register's
  // 8-100 chars range — backend will also re-validate.
  const validate = (): FieldErrors => {
    const errs: FieldErrors = {}
    if (!firstName.trim()) {
      errs.firstName = 'Please enter your first name'
    }
    if (!lastName.trim()) {
      errs.lastName = 'Please enter your last name'
    }
    if (!password.trim()) {
      errs.password = 'Please enter a password'
    } else if (password.length < 8) {
      errs.password = 'Password must be at least 8 characters'
    } else if (password.length > 100) {
      errs.password = 'Password must be at most 100 characters'
    }
    return errs
  }
  // Clears one field's error the moment the user starts editing it again.
  const clearFieldError = (key: keyof FieldErrors) => {
    setFieldErrors((prev) => {
      if (!prev[key]) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
  }
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    // Ignore a second submit while one is in flight (Enter + click) — a
    // duplicate POST would hit the now-used token and show "already accepted".
    if (loading) return
    setError(null)
    const errs = validate()
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs)
      return
    }
    try {
      setLoading(true)
      setError(null)
      setFieldErrors({})
      const auth = await acceptInviteApi({
        token,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        password,
      })
      // Mirror LoginScreen's pattern — setAuth(token, user) lands the
      // user straight in the authenticated app. No post-submit redirect
      // to login; no second sign-in step.
      setAuth(auth.token, auth.user)
      onAccepted?.()
    } catch (err) {
      if (axios.isAxiosError<{ error?: unknown }>(err) && err.response) {
        const backendMessage = err.response.data?.error
        const status = err.response.status
        const messageStr = typeof backendMessage === 'string' ? backendMessage : ''
        if (status === 410) {
          // 410 Gone — invite expired / revoked / already accepted. The
          // backend's specific message is the most useful copy here, so
          // surface it directly rather than hard-coding a generic string.
          setError(messageStr || 'This invite link is no longer valid.')
        } else if (status === 409) {
          // 409 Conflict — an account with this email already exists.
          // Hard-coded copy because the backend's exact wording may vary
          // and the actionable hint ("sign in instead") is frontend-owned.
          setError('An account with this email already exists. Please sign in instead.')
        } else if (status === 400) {
          // 400 — validation failure server-side (password too short,
          // missing field, etc.). Surface the backend message directly.
          setError(messageStr || 'Please check your details and try again.')
        } else if (messageStr.length > 0) {
          // Any other backend message (rate limit, 404 mid-flight, etc.).
          setError(messageStr)
        } else {
          setError('Something went wrong. Please try again.')
        }
      } else {
        // No response received → network failure (offline, DNS, CORS,
        // timeout, server unreachable). Or a non-axios error.
        setError('Could not connect to the server. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }
  // Base input class — border colour lives in fieldClass() below so we
  // can flip to red without fighting Tailwind's class-order resolution.
  const inputBase = `w-full border rounded-lg px-4 py-3
    text-base bg-casevo-input text-casevo-forest focus:outline-none
    focus:border-casevo-mid disabled:opacity-60 disabled:cursor-not-allowed`
  const fieldClass = (key: keyof FieldErrors) =>
    `${inputBase} ${fieldErrors[key] ? 'border-red-500' : 'border-casevo-border'}`
  // Inline spinner — `currentColor` lets it inherit the button's text
  // colour so it works on both the brand-green button and the loading
  // view's surrounding text.
  const Spinner: React.FC<{ size?: number }> = ({ size = 4 }) => (
    <svg
      className={`animate-spin h-${size} w-${size}`}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4Z"
      />
    </svg>
  )
  // Nav bar — same recipe as LoginScreen / AcceptResetScreen / etc.
  // Extracted into a constant because all three render branches use it.
  const navBar = (
    <nav className="bg-casevo-header px-4 sm:px-6 h-14 flex items-center border-b border-casevo-border">
      <div className="flex items-center gap-3">
        <svg width="28" height="28" viewBox="0 0 68 68">
          <circle cx="34" cy="34" r="34" fill="#2d6a4f"/>
          <rect x="23" y="17" width="9" height="34" rx="4.5" fill="#ffffff"/>
          <rect x="36" y="22" width="9" height="24" rx="4.5" fill="#74c69d"/>
        </svg>
        <div className="flex flex-col">
          <span className="text-white font-medium text-sm tracking-widest uppercase leading-none">Casevo</span>
          <span className="text-casevo-light text-xs tracking-widest uppercase leading-none">Case Management</span>
        </div>
      </div>
    </nav>
  )
  // ── BRANCH A: mount-time verification spinner ───────────────
  if (verifying) {
    return (
      <div className="min-h-screen bg-casevo-offwhite font-sans flex flex-col">
        {navBar}
        <div className="flex-1 flex items-center justify-center px-4 py-8">
          <div className="w-full max-w-sm bg-casevo-surface border border-casevo-border rounded-[14px] shadow-sm p-6 sm:p-8">
            <div className="mb-6">
              <p className="text-xs uppercase tracking-widest text-casevo-brand mb-2">Team invite</p>
              <h1 className="text-2xl font-medium text-casevo-forest">Checking your invite…</h1>
            </div>
            <div className="text-casevo-mid">
              <Spinner size={6} />
            </div>
          </div>
        </div>
      </div>
    )
  }
  // ── BRANCH B: token invalid / expired ──────────────────────
  if (tokenInvalid || !invite) {
    return (
      <div className="min-h-screen bg-casevo-offwhite font-sans flex flex-col">
        {navBar}
        <div className="flex-1 flex items-center justify-center px-4 py-8">
          <div className="w-full max-w-sm bg-casevo-surface border border-casevo-border rounded-[14px] shadow-sm p-6 sm:p-8">
            <div className="mb-6">
              <p className="text-xs uppercase tracking-widest text-casevo-brand mb-2">Team invite</p>
              <h1 className="text-2xl font-medium text-casevo-forest">Invite invalid or expired</h1>
            </div>
            <p className="text-sm text-casevo-mid mb-6">
              This invite link is invalid or has expired. Please ask your firm admin to send you a new one.
            </p>
            <button
              type="button"
              onClick={onShowLogin}
              className="w-full bg-casevo-primary text-white text-sm py-3 rounded-lg hover:bg-casevo-primary-hover transition-colors uppercase tracking-widest disabled:opacity-50 font-medium flex items-center justify-center gap-2"
            >
              Back to login
            </button>
          </div>
        </div>
      </div>
    )
  }
  // ── BRANCH C: default form view ────────────────────────────
  return (
    <div className="min-h-screen bg-casevo-offwhite font-sans flex flex-col">
      {navBar}
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-sm bg-casevo-surface border border-casevo-border rounded-[14px] shadow-sm p-6 sm:p-8">
          <div className="mb-6">
            <p className="text-xs uppercase tracking-widest text-casevo-brand mb-2">Team invite</p>
            <h1 className="text-2xl font-medium text-casevo-forest">
              You've been invited to join {invite.firmName}
            </h1>
            <p className="text-sm text-casevo-mid mt-2">
              as <span className="font-medium text-casevo-forest">{invite.email}</span>
            </p>
          </div>
          <p className="text-sm text-casevo-mid mb-6">
            Set your name and a password to accept the invite and sign in.
          </p>
          <form onSubmit={handleSubmit}>
            {error && (
              <ErrorNote className="mb-4">{error}</ErrorNote>
            )}
            {/* First name */}
            <div className="mb-4">
              <label className="block text-xs uppercase tracking-widest text-casevo-brand mb-2">
                First name
              </label>
              <input
                ref={firstNameRef}
                type="text"
                value={firstName}
                onChange={(e) => {
                  setFirstName(e.target.value)
                  clearFieldError('firstName')
                }}
                placeholder="Jane"
                className={fieldClass('firstName')}
                autoComplete="given-name"
                disabled={loading}
                aria-invalid={!!fieldErrors.firstName}
              />
              {fieldErrors.firstName && (
                <p className="text-xs text-casevo-danger mt-1">{fieldErrors.firstName}</p>
              )}
            </div>
            {/* Last name */}
            <div className="mb-4">
              <label className="block text-xs uppercase tracking-widest text-casevo-brand mb-2">
                Last name
              </label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => {
                  setLastName(e.target.value)
                  clearFieldError('lastName')
                }}
                placeholder="Smith"
                className={fieldClass('lastName')}
                autoComplete="family-name"
                disabled={loading}
                aria-invalid={!!fieldErrors.lastName}
              />
              {fieldErrors.lastName && (
                <p className="text-xs text-casevo-danger mt-1">{fieldErrors.lastName}</p>
              )}
            </div>
            {/* Password */}
            <div className="mb-6">
              <label className="block text-xs uppercase tracking-widest text-casevo-brand mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value)
                    clearFieldError('password')
                  }}
                  placeholder="Minimum 8 characters"
                  className={`${fieldClass('password')} pr-12`}
                  autoComplete="new-password"
                  disabled={loading}
                  aria-invalid={!!fieldErrors.password}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-casevo-mid hover:text-casevo-forest text-xs uppercase tracking-widest disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
              {fieldErrors.password && (
                <p className="text-xs text-casevo-danger mt-1">{fieldErrors.password}</p>
              )}
            </div>
            <button
              type="submit"
              disabled={loading}
              aria-busy={loading}
              className="w-full bg-casevo-primary text-white text-sm py-3 rounded-lg hover:bg-casevo-primary-hover transition-colors uppercase tracking-widest disabled:opacity-50 font-medium flex items-center justify-center gap-2"
            >
              {loading && <Spinner />}
              {loading ? 'Accepting…' : 'Accept invite'}
            </button>
          </form>
          <p className="text-center text-sm text-casevo-mid mt-6">
            <button type="button" onClick={onShowLogin} className="text-casevo-brand underline">
              Back to login
            </button>
          </p>
        </div>
      </div>
    </div>
  )
}
export default AcceptInviteScreen
